import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { CATEGORIES, TYPE_LABELS } from "../lib/categories";
import ListingCard from "../components/ListingCard";
import { ArrowRight, Search, Bell, Gavel, Gift, MapPin, Sparkles } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/e14c2c1a-6f8b-4a5e-bf0e-a9ccfd60ec4a/images/52816837b3d7be5f5193578567cc427fe77c5d4feecd71fc3b361474c63629db.png";
const CAT_IMAGES = {
  tools: "https://images.unsplash.com/photo-1601924925166-22a19c485db7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxnYXJhZ2UlMjB0b29scyUyMHdvcmtzaG9wfGVufDB8fHx8MTc3ODE3NTIyMnww&ixlib=rb-4.1.0&q=85",
  auto_parts: "https://images.unsplash.com/photo-1768387666438-b3da75373846?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxjYXIlMjBlbmdpbmUlMjBwYXJ0c3xlbnwwfHx8fDE3NzgxNzUyMjJ8MA&ixlib=rb-4.1.0&q=85",
  garden: "https://images.pexels.com/photos/6728919/pexels-photo-6728919.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  construction: "https://images.unsplash.com/photo-1599256871787-737fd3315df2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxnYXJhZ2UlMjB0b29scyUyMHdvcmtzaG9wfGVufDB8fHx8MTc3ODE3NTIyMnww&ixlib=rb-4.1.0&q=85",
};

export default function Landing() {
  const { lang, t } = useI18n();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [_sp] = useSearchParams();
  const [hot, setHot] = useState([]);
  const [free, setFree] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [stats, setStats] = useState({ listings: 0, users: 0 });
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/listings", { params: { limit: 8 } }).then((r) => setHot(r.data)).catch(()=>{});
    api.get("/listings", { params: { listing_type: "free", limit: 6 } }).then((r) => setFree(r.data)).catch(()=>{});
    api.get("/listings", { params: { listing_type: "auction", limit: 6 } }).then((r) => setAuctions(r.data)).catch(()=>{});
    api.get("/stats").then((r) => setStats(r.data)).catch(()=>{});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/browse?q=${encodeURIComponent(search)}`);
  };

  return (
    <div className="bg-[#FAF9F6]">
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAF9F6] via-[#FAF9F6]/85 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#2A5C38] text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
              <Sparkles className="w-3.5 h-3.5"/> {t("hot_now")} · {stats.listings} {t("nav_browse").toLowerCase()}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tighter text-[#1A1A18] leading-[0.95]">
              {t("tagline")}
            </h1>
            <p className="mt-6 text-lg text-[#4A4A45] max-w-xl leading-relaxed">
              {t("hero_sub")}
            </p>

            <form onSubmit={submitSearch} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="flex items-center bg-white border-2 border-[#1A1A18] rounded-md flex-1 hard-shadow">
                <Search className="w-5 h-5 ml-4 text-[#7A7A73]" />
                <input
                  data-testid="hero-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("search_placeholder")}
                  className="w-full px-3 py-3 outline-none text-base bg-transparent"
                />
              </div>
              <button
                type="submit"
                data-testid="hero-search-button"
                className="btn-primary flex items-center justify-center gap-2"
              >
                {t("search")} <ArrowRight className="w-4 h-4"/>
              </button>
            </form>

            <div className="mt-6 flex flex-wrap gap-3">
              {user ? (
                <button
                  onClick={() => navigate("/post")}
                  data-testid="hero-cta-post"
                  className="btn-secondary"
                >
                  {t("cta_post")} →
                </button>
              ) : (
                <button
                  onClick={login}
                  data-testid="hero-cta-login"
                  className="btn-secondary"
                >
                  {t("cta_login")} →
                </button>
              )}
              <Link to="/browse" data-testid="hero-cta-browse" className="px-6 py-3 text-[#1A1A18] font-semibold hover:underline">
                {t("cta_browse")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#7A7A73]">
              {t("explore_categories")}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight text-[#1A1A18] mt-2">
              Visa, kas slypi tavo garaže
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const img = CAT_IMAGES[c.key];
            return (
              <Link
                key={c.key}
                to={`/browse?category=${c.key}`}
                data-testid={`cat-${c.key}`}
                className="card-surface relative aspect-square overflow-hidden flex flex-col justify-end p-4 group"
              >
                {img ? (
                  <>
                    <img src={img} alt={c[lang]} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-110 transition-all duration-500"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/90 via-[#1A1A18]/30 to-transparent"/>
                  </>
                ) : (
                  <div className="absolute inset-0 bg-[#F0EFEB]"/>
                )}
                <div className="relative">
                  <Icon className={`w-6 h-6 ${img ? "text-white" : "text-[#1A1A18]"} mb-2`} />
                  <p className={`font-heading font-bold text-sm ${img ? "text-white" : "text-[#1A1A18]"}`}>
                    {c[lang]}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* HOT LISTINGS */}
      {hot.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-end justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight text-[#1A1A18]">
              {t("hot_now")}
            </h2>
            <Link to="/browse" className="text-sm font-semibold text-[#C85A32] hover:underline" data-testid="hot-view-all">
              {t("cta_browse")} →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {hot.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        </section>
      )}

      {/* FEATURE CALLOUTS BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Smart Alerts */}
        <div className="card-surface bg-[#2A5C38] hover:!border-[#2A5C38] p-6 lg:col-span-1 group">
          <Bell className="w-7 h-7 text-white mb-3" />
          <h3 className="text-2xl font-heading font-bold text-white">{t("smart_alerts_title")}</h3>
          <p className="text-white/85 text-sm mt-2 leading-relaxed">{t("smart_alerts_sub")}</p>
          <Link to={user ? "/alerts" : "#"} onClick={!user ? login : undefined} className="inline-flex items-center gap-2 mt-4 bg-white text-[#2A5C38] font-semibold px-4 py-2 rounded-md hover:bg-[#DDA74F] hover:text-[#1A1A18] transition-colors" data-testid="cta-alerts">
            {t("add_alert")} <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>

        {/* Free section */}
        <Link to="/browse?listing_type=free" className="card-surface bg-[#FAF9F6] p-6 group" data-testid="cta-free-section">
          <Gift className="w-7 h-7 text-[#C85A32] mb-3" />
          <h3 className="text-2xl font-heading font-bold text-[#1A1A18]">{t("free_section_title")}</h3>
          <p className="text-[#4A4A45] text-sm mt-2 leading-relaxed">{t("free_section_sub")}</p>
          {free.length > 0 && (
            <div className="mt-4 flex -space-x-2">
              {free.slice(0,4).map((l) => (
                <div key={l.id} className="w-12 h-12 rounded-md border-2 border-[#FAF9F6] bg-[#F0EFEB] overflow-hidden">
                  {l.images?.[0] && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${l.images[0]}`} alt="" className="w-full h-full object-cover"/>}
                </div>
              ))}
            </div>
          )}
        </Link>

        {/* Auction */}
        <Link to="/browse?listing_type=auction" className="card-surface bg-[#1A1A18] hover:!border-[#1A1A18] p-6 group" data-testid="cta-auctions">
          <Gavel className="w-7 h-7 text-[#DDA74F] mb-3" />
          <h3 className="text-2xl font-heading font-bold text-white">{t("auctions_title")}</h3>
          <p className="text-white/80 text-sm mt-2 leading-relaxed">{t("auctions_sub")}</p>
          <span className="inline-flex items-center gap-2 mt-4 text-[#DDA74F] font-semibold">
            {auctions.length} {t("nav_browse").toLowerCase()} <ArrowRight className="w-4 h-4"/>
          </span>
        </Link>
      </section>

      {/* NEIGHBORHOOD */}
      <section className="bg-[#F0EFEB] pegboard-bg py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#C85A32]">
                <MapPin className="w-3 h-3 inline -mt-1 mr-1"/>{t("nearby_title")}
              </p>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-[#1A1A18] mt-2">
                {t("distance_title")}
              </h2>
              <p className="text-[#4A4A45] mt-4 text-lg">{t("distance_sub")}</p>
              <Link to="/browse" className="inline-flex items-center gap-2 mt-6 btn-primary" data-testid="cta-neighborhood">
                {t("cta_browse")} <ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {hot.slice(0,4).map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
